import React from 'react';
import { FileJson, FileCheck2, FolderOpen } from 'lucide-react';
import { formatBytes } from '../../utils/formatBytes';
import Tooltip from '../ui/Tooltip';

/**
 * Where the file comes in. Empty: a drop zone (click, Enter/Space or drop — a file OR a Bruno
 * collection folder), plus one inline "Choose folder" link for picking a folder (owner, 27.09.2026:
 * "drop zone + link", no second button). With a file or folder: ONE quiet row — its name, size,
 * what it was recognised as, and "Replace" — so the dialog says which one it is looking at.
 *
 * @param {{ file: {name: string, size: number, folder?: boolean} | null, formatLabel?: string,
 *           dragOver: boolean, busy: boolean, onPick: () => void, onPickFolder?: () => void,
 *           dragHandlers: object, t: Function }} props
 */
export default function ImportFileZone({ file, formatLabel, dragOver, busy, onPick, onPickFolder, dragHandlers, t }) {
  if (file) {
    const Icon = file.folder ? FolderOpen : FileCheck2;
    return (
      <div className={`imp-file${dragOver ? ' imp-file--over' : ''}`} {...dragHandlers}>
        <Icon size={16} className="imp-file__icon" aria-hidden />
        {/* the name is ellipsized, and knowing WHICH file is loaded is the point of this row */}
        <Tooltip content={file.name} side="bottom-start">
          <span className="imp-file__name">{file.name}</span>
        </Tooltip>
        <span className="imp-file__meta">{formatBytes(file.size)}</span>
        {formatLabel && <span className="imp-file__format">{formatLabel}</span>}
        <button type="button" className="imp-file__replace" onClick={onPick} disabled={busy}>
          {t('import.replaceFile', { defaultValue: 'Replace' })}
        </button>
        {/* a folder must stay reachable once something is loaded (reviewer N5) */}
        {onPickFolder && (
          <button type="button" className="imp-file__replace" onClick={onPickFolder} disabled={busy}>
            {t('import.chooseFolder')}
          </button>
        )}
      </div>
    );
  }
  // The zone is a plain container (drop target + outline). Its click/keyboard target and the
  // "Choose folder" link are SIBLINGS inside it: ARIA `button` has presentational children, so a
  // button nested in role="button" can vanish for a screen reader (reviewer N4).
  return (
    <div className={`imp-zone${dragOver ? ' imp-zone--over' : ''}${busy ? ' imp-zone--busy' : ''}`} {...dragHandlers}>
      <div
        role="button"
        tabIndex={0}
        aria-label={t('import.dropHint')}
        className="imp-zone__hit"
        onClick={onPick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } }}
      >
        <FileJson size={26} strokeWidth={1.5} className="imp-zone__icon" aria-hidden />
        <div className="imp-zone__title">{dragOver ? t('import.dropRelease') : t('import.dropHint')}</div>
        <div className="imp-zone__hint">{t('import.dropFormats')}</div>
      </div>
      {onPickFolder && (
        <div className="imp-zone__folder">
          {t('import.chooseFolderPrompt')}{' '}
          <button
            type="button"
            className="imp-zone__folder-link"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onPickFolder(); }}
          >
            {t('import.chooseFolder')}
          </button>
        </div>
      )}
    </div>
  );
}
